/**
 * Growth Chart
 *
 * Horizontal bars for growth areas with trend arrows.
 * Coaching themes as tag chips.
 */

import { motion } from 'framer-motion'
import type { GrowthArea, TrendDirection } from '@personic/shared'

interface GrowthChartProps {
  growthAreas: GrowthArea[]
  coachingThemes: string[]
  overallTrend: TrendDirection
}

const trendConfig: Record<TrendDirection, { icon: string; color: string; label: string }> = {
  improving: { icon: '↑', color: 'text-green-600', label: 'Improving' },
  stable: { icon: '→', color: 'text-blue-600', label: 'Stable' },
  declining: { icon: '↓', color: 'text-red-600', label: 'Declining' },
  insufficient_data: { icon: '?', color: 'text-gray-400', label: 'Insufficient Data' },
  consent_not_granted: { icon: '🔒', color: 'text-gray-400', label: 'Consent Required' },
}

export default function GrowthChart({
  growthAreas,
  coachingThemes,
  overallTrend,
}: GrowthChartProps) {
  const overallConfig = trendConfig[overallTrend] || trendConfig.stable

  return (
    <div className="space-y-6">
      {/* Overall Trend */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-700">Overall Trend:</span>
        <span className={`flex items-center gap-1 text-sm font-semibold ${overallConfig.color}`}>
          <span>{overallConfig.icon}</span>
          <span>{overallConfig.label}</span>
        </span>
      </div>

      {/* Growth Area Bars */}
      {growthAreas.length > 0 ? (
        <div className="space-y-4">
          {growthAreas.map((area, index) => {
            const trend = trendConfig[area.trend] || trendConfig.stable
            const barWidth = Math.min(area.sessions_observed * 10, 100)
            return (
              <motion.div
                key={area.area}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-800">
                    {area.area}
                  </span>
                  <span className={`flex items-center gap-1 text-xs ${trend.color}`}>
                    <span>{trend.icon}</span>
                    <span>{trend.label}</span>
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${barWidth}%` }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`h-2.5 rounded-full ${
                      area.trend === 'improving'
                        ? 'bg-green-500'
                        : area.trend === 'declining'
                        ? 'bg-red-400'
                        : 'bg-blue-400'
                    }`}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {area.detail}
                  <span className="text-gray-400 ml-2">
                    ({area.sessions_observed} session{area.sessions_observed !== 1 ? 's' : ''})
                  </span>
                </p>
              </motion.div>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-6">
          <p className="text-sm text-gray-500">No growth data available yet</p>
        </div>
      )}

      {/* Coaching Themes */}
      {coachingThemes.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Coaching Themes
          </h4>
          <div className="flex flex-wrap gap-2">
            {coachingThemes.map((theme) => (
              <span
                key={theme}
                className="text-xs px-3 py-1.5 bg-engage-primary/10 text-engage-primary rounded-full font-medium"
              >
                {theme}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
